import { ChangeDetectorRef, Component, ElementRef, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { AnykLapComponent } from '../anyk-lap/anyk-lap.component';
import { AnykKotes } from '../anyk-kotes';
import { NavNyomtatvanyService } from '../nav-nyomtatvany.service';
import { onalloHtml } from '../nyomtatas-keret';
import { NyomtatasLap, nyomtatandoLapok } from '../nyomtatas-lapok';

/**
 *  BEVALLÁS PDF-KÉNT – a kitöltő megnyitása és a böngésző nyomtatóablaka nélkül (munkaterv, user 2026-10-07).
 *
 *  A lapokat egy láthatatlan tárolóban rendereljük (ugyanaz a lapkép, mint a nyomtatásnál), önálló
 *  HTML-t készítünk belőlük (beágyazott stílus és kép), a kiszolgáló valódi PDF-et csinál belőle
 *  (`POST /nav-nyomtatvany/bevallas/:id/pdf`), és a PDF magában nyílik meg új lapon.
 *
 *  Használat: `<app-bevallas-pdf #pdf></app-bevallas-pdf>` + `pdf.megnyit({ id, kod, cim })`.
 */
@Component({
  selector: 'app-bevallas-pdf',
  standalone: true,
  imports: [CommonModule, AnykLapComponent],
  template: `
    <div class="any-nyomtat" *ngIf="lapok.length">
      <div class="any-nyomtat-lap" *ngFor="let t of lapok"
           [style.width.px]="(t.oldal.w + 20) * t.arany" [style.height.px]="(t.oldal.h + 20) * t.arany">
        <div class="any-nyomtat-tart" [style.transform]="'scale(' + t.arany + ')'">
          <app-anyk-lap [oldal]="t.oldal" [hely]="t.hely" [mezo]="t.mezo" [csakOlvas]="true" [logo]="logo"
                        [matrixok]="t.matrixok" [metak]="t.metak"></app-anyk-lap>
        </div>
      </div>
    </div>`,
  //  A tároló a képernyőn kívül áll (nem `display:none`: a lapkép méretei így valódiak).
  styles: [':host { position: fixed; left: -20000px; top: 0; width: 900px; pointer-events: none; }'],
})
export class BevallasPdfComponent {
  /** A lapkép fejlécének ikonja (a modul címere). */
  @Input() logo: string | null = null;
  lapok: NyomtatasLap[] = [];
  fut = false;
  private kotesTar = new Map<string, AnykKotes>();

  constructor(private ny: NavNyomtatvanyService, private http: HttpClient, private host: ElementRef<HTMLElement>,
              private cd: ChangeDetectorRef) {}

  /**
   *  A PDF elkészítése és megnyitása új lapon. A lapot a kattintás pillanatában nyitjuk meg (üresen),
   *  különben a böngésző előugró ablakként letiltaná; addig egy „készül” felirat áll rajta.
   *  Hibánál a lap bezárul, és a hívó kapja a hibát.
   */
  async megnyit(b: { id: string; kod: string; cim?: string }): Promise<void> {
    if (this.fut) return;
    const lap = window.open('', '_blank');
    try { lap?.document.write(`<!doctype html><meta charset="utf-8"><title>${b.kod} – PDF készül…</title>`
      + '<p style="font:16px system-ui,sans-serif;margin:40px;color:#555">A bevallás PDF-je készül…</p>'); } catch { /* */ }
    this.fut = true;
    try {
      let nincsLapkep = false;
      this.lapok = await nyomtatandoLapok(this.ny, [b], this.kotesTar, () => { nincsLapkep = true; });
      if (nincsLapkep) throw new Error(`${b.kod}: ehhez a nyomtatványhoz nincs ÁNYK-lapkép, PDF csak a kitöltőből (nyomtatással) készíthető.`);
      if (!this.lapok.length) throw new Error('A bevallásban nincs nyomtatható (kitöltött) lap.');
      this.cd.detectChanges();
      await new Promise((ok) => setTimeout(ok, 300));            // a lapkép-komponensek kirajzolása
      const forras = this.host.nativeElement.querySelector('.any-nyomtat') as HTMLElement | null;
      if (!forras) throw new Error('A nyomtatványkép nem készült el.');
      const html = await onalloHtml(forras, `${b.kod}${b.cim ? ' – ' + b.cim : ''}`);
      const pdf = await firstValueFrom(this.http.post(`/api/nav-nyomtatvany/bevallas/${b.id}/pdf`, { html }, { responseType: 'blob' }));
      const url = URL.createObjectURL(new Blob([pdf], { type: 'application/pdf' }));
      if (lap && !lap.closed) lap.location.href = url; else window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 10 * 60 * 1000);
    } catch (e: any) {
      try { lap?.close(); } catch { /* */ }
      //  A blobként kért válasz hibaüzenete is blob – kiolvassuk.
      let uzenet = e?.message || 'Nem sikerült elkészíteni a PDF-et.';
      if (e?.error instanceof Blob) { try { uzenet = JSON.parse(await e.error.text())?.error || uzenet; } catch { /* */ } }
      throw new Error(uzenet);
    } finally {
      this.lapok = [];
      this.fut = false;
      this.cd.detectChanges();
    }
  }
}
